/**
 * AdsService — Multi-Platform Ad Service
 *
 * Integração centralizada que detecta a plataforma e delega para:
 *   • Web (browser/PWA)  → Google H5 Games Ads (Ad Placement API)
 *   • Android (Capacitor) → AdMob via @capacitor-community/admob
 *
 * Zero requests quando ads desativados (feature flag remota via admin panel).
 * Script/SDK carregado dinamicamente — não polui index.html.
 *
 * O fluxo de recompensas (hooks: beforeAd, afterAd, adViewed, adDismissed,
 * adBreakDone) é idêntico em ambas as plataformas — callers não precisam
 * saber qual provider está rodando.
 */

import { Capacitor } from '@capacitor/core';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Configuração vinda do servidor (GET /api/config/ads) */
export interface AdsRemoteConfig {
  enabled: boolean;
  interstitial: { enabled: boolean };
  rewarded: { enabled: boolean };
  preroll: { enabled: boolean };
  testMode: boolean;
  /** AdMob config — usado apenas no Android nativo */
  admob?: {
    appId: string;
    rewardedAdUnitId: string;
    interstitialAdUnitId: string;
  };
}

export type AdPlatform = 'web' | 'android' | 'disabled';

/** Tipos de placement aceitos pela Ad Placement API */
export type AdPlacementType =
  | 'preroll'
  | 'start'
  | 'pause'
  | 'next'
  | 'browse'
  | 'reward';

/** Resultado retornado por adBreakDone */
export interface AdBreakDoneInfo {
  breakType: AdPlacementType;
  breakName: string;
  breakFormat: string;
  breakStatus:
    | 'viewed'
    | 'dismissed'
    | 'notReady'
    | 'frequencyCapped'
    | 'error'
    | 'other';
}

/** Hooks para interstitial */
export interface InterstitialHooks {
  beforeAd?: () => void;
  afterAd?: () => void;
  adBreakDone?: (info: AdBreakDoneInfo) => void;
}

/** Hooks para rewarded */
export interface RewardedHooks {
  beforeAd?: () => void;
  afterAd?: () => void;
  adDismissed?: () => void;
  adViewed?: () => void;
  adBreakDone?: (info: AdBreakDoneInfo) => void;
}

// ---------------------------------------------------------------------------
// Globals (declarados pelo Google SDK — web only)
// ---------------------------------------------------------------------------

declare global {
  interface Window {
    adsbygoogle: any[];
    adBreak: (config: any) => void;
    adConfig: (config: any) => void;
  }
}

// ---------------------------------------------------------------------------
// AdsService singleton
// ---------------------------------------------------------------------------

class AdsService {
  private config: AdsRemoteConfig | null = null;
  private platform: AdPlatform = 'disabled';
  private scriptLoaded = false;
  private ready = false;
  private clientId: string;

  // AdMob adapter — lazy loaded only on native (never imported on web)
  private admobAdapter: typeof import('./AdMobAdapter') | null = null;

  constructor() {
    this.clientId = import.meta.env.VITE_ADSENSE_CLIENT_ID || '';
  }

  // -----------------------------------------------------------------------
  // init() — chamado uma vez no boot do jogo
  // -----------------------------------------------------------------------

  async init(remoteConfig: AdsRemoteConfig): Promise<void> {
    this.config = remoteConfig;

    if (!remoteConfig.enabled) {
      this.platform = 'disabled';
      this.installStubs();
      console.log('[AdsService] Ads DISABLED by remote config — stubs installed.');
      return;
    }

    // Detect platform: Capacitor native (Android) vs web browser
    const isNative = Capacitor.isNativePlatform();
    this.platform = isNative ? 'android' : 'web';

    if (this.platform === 'android') {
      await this.initAndroid(remoteConfig);
    } else {
      await this.initWeb(remoteConfig);
    }
  }

  // -----------------------------------------------------------------------
  // Android init (AdMob via Capacitor)
  // -----------------------------------------------------------------------

  private async initAndroid(config: AdsRemoteConfig): Promise<void> {
    try {
      // Dynamic import — never bundled in web builds (tree-shaken by Vite)
      this.admobAdapter = await import('./AdMobAdapter');
      const ok = await this.admobAdapter.initAdMob(config.testMode);

      if (ok) {
        this.ready = true;
        console.log('[AdsService] Android AdMob initialized, testMode:', config.testMode);

        // Pre-load ads in background
        const rewardedId = config.admob?.rewardedAdUnitId
          || import.meta.env.VITE_ADMOB_REWARDED_AD_UNIT
          || '';
        const interstitialId = config.admob?.interstitialAdUnitId
          || import.meta.env.VITE_ADMOB_INTERSTITIAL_AD_UNIT
          || '';

        if (rewardedId) {
          this.admobAdapter.prepareRewarded(rewardedId, config.testMode).catch(() => {});
        }
        if (interstitialId) {
          this.admobAdapter.prepareInterstitial(interstitialId, config.testMode).catch(() => {});
        }
      } else {
        this.installStubs();
        console.warn('[AdsService] AdMob init failed — stubs installed.');
      }
    } catch (err) {
      console.error('[AdsService] AdMob dynamic import failed:', err);
      this.installStubs();
    }
  }

  // -----------------------------------------------------------------------
  // Web init (H5 Games Ads)
  // -----------------------------------------------------------------------

  private async initWeb(config: AdsRemoteConfig): Promise<void> {
    if (!this.clientId) {
      this.installStubs();
      console.warn('[AdsService] VITE_ADSENSE_CLIENT_ID not set — stubs installed.');
      return;
    }

    await this.loadH5Script(config.testMode);
    console.log('[AdsService] Web H5 Games Ads loaded, testMode:', config.testMode);
  }

  // -----------------------------------------------------------------------
  // updateSound() — sync com o estado de mute do jogo
  // -----------------------------------------------------------------------

  updateSound(isOn: boolean): void {
    if (!this.config?.enabled || !this.ready) return;
    // adConfig only exists on web — AdMob native handles audio automatically
    if (this.platform === 'web') {
      try { window.adConfig({ sound: isOn ? 'on' : 'off' }); } catch { /* swallow */ }
    }
  }

  // -----------------------------------------------------------------------
  // showInterstitial() — para transições naturais
  // -----------------------------------------------------------------------

  showInterstitial(
    placementType: Exclude<AdPlacementType, 'reward'>,
    name: string,
    hooks: InterstitialHooks = {},
  ): void {
    if (!this.canShowInterstitial()) {
      hooks.adBreakDone?.({
        breakType: placementType,
        breakName: name,
        breakFormat: 'interstitial',
        breakStatus: 'notReady',
      });
      return;
    }

    // Android — delegate to AdMob adapter
    if (this.platform === 'android' && this.admobAdapter) {
      const adUnitId = this.config?.admob?.interstitialAdUnitId
        || import.meta.env.VITE_ADMOB_INTERSTITIAL_AD_UNIT
        || '';
      this.admobAdapter.showInterstitial(name, adUnitId, !!this.config?.testMode, hooks);
      return;
    }

    // Web — H5 Games Ads
    window.adBreak({
      type: placementType,
      name,
      beforeAd: () => {
        console.log(`[AdsService] beforeAd — ${name}`);
        hooks.beforeAd?.();
      },
      afterAd: () => {
        console.log(`[AdsService] afterAd — ${name}`);
        hooks.afterAd?.();
      },
      adBreakDone: (info: AdBreakDoneInfo) => {
        console.log(`[AdsService] adBreakDone — ${name}`, info);
        hooks.adBreakDone?.(info);
      },
    });
  }

  // -----------------------------------------------------------------------
  // showRewarded() — vídeo recompensado (ação do usuário obrigatória)
  // -----------------------------------------------------------------------

  showRewarded(name: string, hooks: RewardedHooks = {}): void {
    if (!this.canShowRewarded()) {
      hooks.adBreakDone?.({
        breakType: 'reward',
        breakName: name,
        breakFormat: 'reward',
        breakStatus: 'notReady',
      });
      return;
    }

    // Android — delegate to AdMob adapter
    if (this.platform === 'android' && this.admobAdapter) {
      const adUnitId = this.config?.admob?.rewardedAdUnitId
        || import.meta.env.VITE_ADMOB_REWARDED_AD_UNIT
        || '';
      this.admobAdapter.showRewarded(name, adUnitId, !!this.config?.testMode, hooks);
      return;
    }

    // Web — H5 Games Ads
    window.adBreak({
      type: 'reward',
      name,
      beforeAd: () => {
        console.log(`[AdsService] beforeAd (reward) — ${name}`);
        hooks.beforeAd?.();
      },
      afterAd: () => {
        console.log(`[AdsService] afterAd (reward) — ${name}`);
        hooks.afterAd?.();
      },
      beforeReward: (showAdFn: () => void) => {
        showAdFn();
      },
      adDismissed: () => {
        console.log(`[AdsService] adDismissed (reward) — ${name}`);
        hooks.adDismissed?.();
      },
      adViewed: () => {
        console.log(`[AdsService] adViewed (reward) — ${name}`);
        hooks.adViewed?.();
      },
      adBreakDone: (info: AdBreakDoneInfo) => {
        console.log(`[AdsService] adBreakDone (reward) — ${name}`, info);
        hooks.adBreakDone?.(info);
      },
    });
  }

  // -----------------------------------------------------------------------
  // Query helpers
  // -----------------------------------------------------------------------

  isEnabled(): boolean {
    return !!this.config?.enabled && this.ready;
  }

  isInterstitialEnabled(): boolean {
    return this.isEnabled() && !!this.config?.interstitial?.enabled;
  }

  isRewardedEnabled(): boolean {
    return this.isEnabled() && !!this.config?.rewarded?.enabled;
  }

  isPrerollEnabled(): boolean {
    return this.isEnabled() && !!this.config?.preroll?.enabled;
  }

  getPlatform(): AdPlatform {
    return this.platform;
  }

  getConfig(): AdsRemoteConfig | null {
    return this.config;
  }

  // -----------------------------------------------------------------------
  // Internals
  // -----------------------------------------------------------------------

  private canShowInterstitial(): boolean {
    return this.isEnabled() && !!this.config?.interstitial?.enabled;
  }

  private canShowRewarded(): boolean {
    return this.isEnabled() && !!this.config?.rewarded?.enabled;
  }

  private installStubs(): void {
    const noop = () => {};
    if (typeof window !== 'undefined') {
      window.adBreak ??= noop;
      window.adConfig ??= noop;
    }
    this.ready = false;
  }

  private loadH5Script(testMode: boolean): Promise<void> {
    return new Promise((resolve) => {
      if (this.scriptLoaded) {
        resolve();
        return;
      }

      // Bootstrap adsbygoogle + proxy functions BEFORE script loads
      window.adsbygoogle = window.adsbygoogle || [];
      window.adBreak = window.adConfig = (o: any) => {
        window.adsbygoogle.push(o);
      };

      const script = document.createElement('script');
      script.async = true;
      script.setAttribute('data-rocketanks-ads', '1');
      if (testMode) {
        script.setAttribute('data-adbreak-test', 'on');
      }
      script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${encodeURIComponent(this.clientId)}`;
      script.crossOrigin = 'anonymous';

      script.onload = () => {
        this.scriptLoaded = true;
        this.ready = true;

        // Configuração inicial
        window.adConfig({
          preloadAdBreaks: 'on',
          sound: 'on',
          onReady: () => {
            console.log('[AdsService] H5 Games Ads SDK ready');
          },
        });

        resolve();
      };

      script.onerror = () => {
        console.error('[AdsService] Failed to load H5 Games Ads SDK');
        this.installStubs();
        resolve(); // Don't reject — jogo continua sem ads
      };

      document.head.appendChild(script);
    });
  }
}

// ---------------------------------------------------------------------------
// Export singleton
// ---------------------------------------------------------------------------

export const adsService = new AdsService();
